import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import { useUserState } from "../../../state/user";
import { updateOccupationSpecialty } from "../../../services/professionals";
import { foldAccents } from "@kenettotech/scribo-shared-functions";
import { outlinedFieldSx } from "../../../utils/fieldStyles";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

export interface Specialty {
  code: number;
  local: string;
}

const listSx = {
  maxHeight: "45vh",
  overflowY: "auto",
  mt: 2,
  borderRadius: "8px",
  border: `1px solid ${semanticColors.border.light}`
};

const itemSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

const selectedSx = {
  backgroundColor: semanticColors.surface.containerLow
};

const errorSx = { mt: 2 };

const emptySx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  p: 2,
  textAlign: "center" as const
};

/**
 * Picks a specialty from the occupation's own list.
 *
 * The list is long — 52 for a doctor — so it is filtered as well as scrolled,
 * the same two ways the mobile offers. Matching folds accents so that typing
 * "pediatria" finds "Pediatria" and "cirurgia cardiaca" finds the accented
 * spelling; the folding is the shared one, so both apps match alike.
 */
export default function UpdateSpecialtyDialog({
  open,
  onClose,
  specialties,
  currentCode,
  onUpdated
}: {
  open: boolean;
  onClose: () => void;
  specialties: Specialty[];
  currentCode?: number;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const { token, refreshUser } = useUserState();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<number | undefined>(currentCode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = useMemo(() => {
    const needle = foldAccents(query.trim().toLowerCase());
    if (!needle) return specialties;
    return specialties.filter(specialty =>
      foldAccents(specialty.local.toLowerCase()).includes(needle)
    );
  }, [specialties, query]);

  const onSubmit = useCallback(async () => {
    if (!token || selected === undefined) return;
    setSaving(true);
    setError(null);
    try {
      await updateOccupationSpecialty({
        authToken: token,
        occupationSpecialty: selected
      });
      await refreshUser();
      onUpdated();
    } catch {
      setError(t("professionalData.specialtyError"));
    } finally {
      setSaving(false);
    }
  }, [token, selected, refreshUser, onUpdated, t]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("professionalData.updateSpecialty")}
      maxWidth="sm"
    >
      <DialogContent>
        <TextField
          label={t("common.search")}
          value={query}
          onChange={event => setQuery(event.target.value)}
          fullWidth
          sx={outlinedFieldSx}
        />

        <Box sx={listSx}>
          {matches.length === 0 ? (
            <Typography sx={emptySx}>{t("common.noResults")}</Typography>
          ) : (
            <List disablePadding>
              {matches.map(specialty => (
                <ListItemButton
                  key={specialty.code}
                  selected={selected === specialty.code}
                  onClick={() => setSelected(specialty.code)}
                  sx={selected === specialty.code ? selectedSx : undefined}
                >
                  <Typography sx={itemSx}>{specialty.local}</Typography>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={errorSx}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("professionalData.update")}
            onClick={onSubmit}
            loading={saving}
            disabled={selected === undefined || selected === currentCode}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
